import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import {
  useModals,
  Modal,
  ModalProps,
  Box,
  Button,
  Input,
  Radio,
  Select,
  Stack,
  Textarea,
} from "@skailer-com/uikit";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import i18n from "@/i18n.ts";
import { useEmployeeTimeOffCategoriesData } from "@/queries/employees/use-employees.query.ts";
import { useTimeOffAdjustment } from "@/queries/use-time-off.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiEmployeeTimeOffCategoryPayload } from "@/types/employees/employee.types.ts";
import {
  ApiTimeOffAdjustmentPayload,
  ApiTimeOffAdjustmentType,
} from "@/types/time-off/time-off.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface EmployeeEditTimeOffBalanceModalProps extends ModalProps {
  adjustment: ApiTimeOffAdjustmentPayload;
  adjustmentId?: number;
}

export const EmployeeEditTimeOffBalanceModal = ({
  adjustment,
  adjustmentId,
  ...props
}: EmployeeEditTimeOffBalanceModalProps) => {
  const { t } = useTranslation();
  const { id } = { ...props };
  const { close } = useModals();

  const [payloadCategory] = useState<ApiEmployeeTimeOffCategoryPayload>({
    date_balance: new Date(),
  });

  const { data: categories, isLoading: isCategoriesLoading } = useEmployeeTimeOffCategoriesData(
    adjustment.employee_id,
    payloadCategory
  );

  const isUpdate = !!adjustmentId;

  const BalanceSchemaObject = z.object({
    category_id: z.number({ error: i18n.t("errors:field_required") }),
    date_expire: z.date().nullable().optional(),
    date_adjustment: z.date({ error: i18n.t("errors:field_required") }),
    balance: z.preprocess(
      (value) => (value === "" || value == null ? undefined : Number(value)),
      z.number({ error: i18n.t("errors:field_required") }).max(999)
    ),
    adjustmentType: z.enum([ApiTimeOffAdjustmentType.increase, ApiTimeOffAdjustmentType.decrease]),
    comment: z.string().nullable().optional(),
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(BalanceSchemaObject),
    defaultValues: {
      adjustmentType:
        adjustment.balance < 0
          ? ApiTimeOffAdjustmentType.decrease
          : ApiTimeOffAdjustmentType.increase,
      date_adjustment: adjustment.date_adjustment,
      category_id: adjustment.category_id,
      balance: Math.abs(adjustment.balance),
      date_expire: adjustment.date_expire,
      comment: adjustment.comment,
    },
  });

  useEffect(() => {
    if (!isUpdate && categories?.length) {
      setValue("category_id", categories[0].id);
    }
  }, [categories]);

  const adjustmentSave = useTimeOffAdjustment(control);

  const onSubmit = handleSubmit((data) => {
    const balance =
      data.adjustmentType === ApiTimeOffAdjustmentType.decrease
        ? -Math.abs(data.balance)
        : Math.abs(data.balance);

    adjustmentSave.mutate(
      {
        id: adjustmentId,
        payload: {
          employee_id: adjustment.employee_id,
          category_id: data.category_id,
          balance,
          date_expire: data.date_expire || null,
          date_adjustment: data.date_adjustment,
          comment: data.comment || "",
        },
      },
      {
        onSuccess: () => {
          analyticsService.trackEvent(analyticEvents.timeOffs.balanceAdjusted, {
            [analyticProperties.id]: data.category_id,
          });
          close(id);
        },
      }
    );
  });

  const adjustmentType = watch("adjustmentType");

  useEffect(() => {
    if (adjustmentType === ApiTimeOffAdjustmentType.decrease) {
      setValue("date_expire", null, { shouldValidate: true, shouldDirty: true });
    }
  }, [adjustmentType, setValue]);

  return (
    <Modal
      {...props}
      variant="middle"
      title={t("time_off:edit_balance")}
      layout="base"
      withCloser={true}
      actions={
        <Stack gap="sm">
          <ServerErrorField errors={errors} />
          <Button
            size="lg"
            className="w-full"
            isLoading={adjustmentSave.isPending}
            onClick={onSubmit}
          >
            {t("common:save")}
          </Button>
          <Button
            variant="plain"
            size="lg"
            onClick={() => {
              close(id);
            }}
          >
            {t("common:cancel")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Stack gap={"lg"}>
          <Stack>
            <Controller
              control={control}
              name="adjustmentType"
              render={({ field }) => (
                <Box variant="border">
                  <Radio
                    value={ApiTimeOffAdjustmentType.increase}
                    checked={field.value === ApiTimeOffAdjustmentType.increase}
                    onChange={() => field.onChange(ApiTimeOffAdjustmentType.increase)}
                    title={t("time_off:increase_balance")}
                  />
                  <Radio
                    value={ApiTimeOffAdjustmentType.decrease}
                    checked={field.value === ApiTimeOffAdjustmentType.decrease}
                    onChange={() => field.onChange(ApiTimeOffAdjustmentType.decrease)}
                    title={t("time_off:decrease_balance")}
                  />
                </Box>
              )}
            />
          </Stack>
          <Stack>
            <Controller
              control={control}
              name="category_id"
              render={({ field }) => (
                <Select
                  label={t("time_off:category")}
                  required
                  options={categories?.map((category) => ({
                    id: category.id,
                    title: category.title,
                  }))}
                  isLoading={isCategoriesLoading}
                  emptyTitle={t("common:select")}
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.category_id?.message}
                  readOnly={isUpdate}
                />
              )}
            />
            {adjustmentType === ApiTimeOffAdjustmentType.increase && (
              <Controller
                control={control}
                name="date_expire"
                render={({ field }) => (
                  <Datepicker
                    label={t("time_off:expiration_date")}
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.date_expire?.message}
                  />
                )}
              />
            )}
            <Controller
              control={control}
              name="date_adjustment"
              render={({ field }) => (
                <Datepicker
                  label={t("common:date")}
                  required
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.date_adjustment?.message}
                />
              )}
            />
            <Input
              label={t("time_off:balance")}
              type="text"
              pattern="[0-9]*"
              inputMode="numeric"
              required
              {...register("balance")}
              error={errors.balance?.message}
            />
            <Textarea label={t("common:comments")} {...register("comment")} />
          </Stack>
        </Stack>
      </form>
    </Modal>
  );
};
